/**
 * A phone number as a person reads it, and as a phone dials it.
 *
 * Clinic numbers arrive as stored — usually E.164 ("+19034561919"), sometimes
 * with spaces or brackets. Shown raw, "+19034561919" is eleven digits to count;
 * shown without its country code, it cannot be dialled by a patient joining a
 * virtual visit from abroad. North American numbers (the only ones a clinic
 * here has) are written "+1 903-456-1919"; anything else is shown as stored,
 * never guessed at. The link always dials the full number.
 */

const NANP = /^1?(\d{3})(\d{3})(\d{4})$/;

function digitsOf(value) {
  const raw = String(value ?? "").trim();
  // Only a phone number's own characters; an extension or a note is left as stored.
  if (!raw || !/^\+?[\d\s().-]+$/.test(raw)) return null;
  return raw.replace(/\D/g, "");
}

/** "+1 903-456-1919" for a North American number; anything else as stored. */
export function formatPhone(value) {
  const digits = digitsOf(value);
  const match = digits && (digits.length === 10 || (digits.length === 11 && digits[0] === "1")) ? digits.match(NANP) : null;
  if (!match) return String(value ?? "").trim();
  return `+1 ${match[1]}-${match[2]}-${match[3]}`;
}

/** The `tel:` link for a number: E.164 when it can be, the stored digits otherwise. */
export function phoneHref(value) {
  const raw = String(value ?? "").trim();
  const digits = digitsOf(value);
  if (!digits) return `tel:${raw.replace(/[^\d+]/g, "")}`;
  if (digits.length === 10) return `tel:+1${digits}`;
  return raw.startsWith("+") || (digits.length === 11 && digits[0] === "1") ? `tel:+${digits}` : `tel:${digits}`;
}
